import { QueryError } from '@graphql-box/helpers';

import { useMultiQuery } from '@graphql-box/react';
import { type Person } from '@tmdb-graphql-api/schema/types';
import { useEffect, useState } from 'react';
import { type SetRequired } from 'type-fest';
import { encodeId } from '#helpers/encodeId.ts';
import { InnerQuery } from './InnerQuery.tsx';
import { mergePopulatedValue } from './helpers/mergePopulatedValue.ts';
import { type OuterQueryProps, type PersonOption } from './types.ts';

const GET_PERSON_DETAILS = "\nquery GetPersonDetails(\n  $id: ID!\n) {\n  person(id: $id) {\n    biography\n    birthday\n    deathday\n    gender\n    knownForDepartment\n    name\n    placeOfBirth\n    popularity\n    profilePath\n    ...PersonMovieCredits\n    ...PersonProfiles\n    ...PersonTvCredits\n  }\n}\n\nfragment PersonMovieCredits on Person {\n  movieCredits {\n    cast {\n      __typename\n      character\n      posterPath\n      title\n      voteAverage\n      voteCount\n    }\n    crew {\n      __typename\n      job\n      posterPath\n      title\n      voteAverage\n      voteCount\n    }\n  }\n}\n\nfragment PersonProfiles on Person {\n  profiles {\n    aspectRadio\n    filePath\n    fileType\n    height\n    width\n  }\n}\n\nfragment PersonTvCredits on Person {\n  tvCredits {\n    cast {\n      __typename\n      character\n      posterPath\n      name\n      voteAverage\n      voteCount\n    }\n    crew {\n      __typename\n      job\n      posterPath\n      name\n      voteAverage\n      voteCount\n    }\n  }\n}\n";

export const OuterQuery = ({ ariaLabelledBy, name, setFieldValue, value }: OuterQueryProps) => {
  const { execute, results = [] } = useMultiQuery<{ person: Person }>(GET_PERSON_DETAILS, {
    loading: true,
  });

  const [populatedValue, setPopulatedValue] = useState<SetRequired<PersonOption, 'name'>[]>([]);
  const valueKey = encodeId(value);

  useEffect(() => {
    const options = value.filter(entry => !entry.name).map(entry => ({ variables: { id: entry.id } }));
    void execute(options, { data: { initiator: 'PersonMultiSelectDropdown' } });
    // We only want to re-execute when valueKey changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  const data: Person[] = [];
  const errors: QueryError[] = [];

  for (const result of results) {
    if (result instanceof QueryError) {
      errors.push(result);
    } else {
      data.push(result.data.person);
    }
  }

  if (errors.length > 0) {
    console.log(errors);
  }

  useEffect(() => {
    if (data.length > 0) {
      const newPopulatedValue = data.map(entry => ({
        id: entry.id,
        knownForDepartment: entry.knownForDepartment,
        name: entry.name,
        profilePath: entry.profilePath,
      }));

      setPopulatedValue(newPopulatedValue);
    }

    // We only want to re-execute when id valueKey or data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey, data.length]);

  const mergedValue = mergePopulatedValue(populatedValue, value);
  return <InnerQuery ariaLabelledBy={ariaLabelledBy} name={name} setFieldValue={setFieldValue} value={mergedValue} />;
};
